import { useState } from 'react';

interface LineDef {
  id: string;
  label: string;
  path: string;
  circles: { cx: number; cy: number; r: number }[];
  colorClass: string;
  strokeWidth: number;
  strokeDasharray: string | null;
}

const lines: LineDef[] = [
  {
    id: 'commission',
    label: 'Commission',
    path: 'M140,120 C220,120 220,185 300,185 C380,185 380,290 460,290 C540,290 540,263 620,263',
    circles: [
      { cx: 140, cy: 120, r: 3.5 },
      { cx: 300, cy: 185, r: 3.5 },
      { cx: 460, cy: 290, r: 3.5 },
      { cx: 620, cy: 263, r: 3.5 },
    ],
    colorClass: 'text-primary-500',
    strokeWidth: 2.5,
    strokeDasharray: null,
  },
  {
    id: 'swap',
    label: 'Swap / Rollover',
    path: 'M140,290 C220,290 220,290 300,290 C380,290 380,160 460,160 C540,160 540,115 620,115',
    circles: [
      { cx: 140, cy: 290, r: 3.5 },
      { cx: 300, cy: 290, r: 3.5 },
      { cx: 460, cy: 160, r: 3.5 },
      { cx: 620, cy: 115, r: 3.5 },
    ],
    colorClass: 'text-accent-500',
    strokeWidth: 2.5,
    strokeDasharray: null,
  },
  {
    id: 'spread',
    label: 'Spread',
    path: 'M140,243 C220,243 220,170 300,170 C380,170 380,180 460,180 C540,180 540,255 620,255',
    circles: [
      { cx: 140, cy: 243, r: 3 },
      { cx: 300, cy: 170, r: 3 },
      { cx: 460, cy: 180, r: 3 },
      { cx: 620, cy: 255, r: 3 },
    ],
    colorClass: 'text-secondary-500',
    strokeWidth: 2,
    strokeDasharray: '6 4',
  },
  {
    id: 'slippage',
    label: 'Slippage (est.)',
    path: 'M140,265 C220,265 220,265 300,265 C380,265 380,280 460,280 C540,280 540,278 620,278',
    circles: [
      { cx: 140, cy: 265, r: 2 },
      { cx: 300, cy: 265, r: 2 },
      { cx: 460, cy: 280, r: 2 },
      { cx: 620, cy: 278, r: 2 },
    ],
    colorClass: 'text-foreground-500',
    strokeWidth: 1.5,
    strokeDasharray: '3 8',
  },
];

/* Mobile-simplified label pairs: [line1, line2] where line2 is '' if single */
const mobileXLabels: [string, string][] = [
  ['Scalping', ''],
  ['Day', 'Trading'],
  ['Swing', 'Trading'],
  ['Position', 'Trading'],
];

const xPositions = [140, 300, 460, 620];

export default function WhichCostDominatesSection() {
  const [activeCost, setActiveCost] = useState<string | null>(null);

  const handleLineTouch = (id: string) => {
    setActiveCost((prev) => (prev === id ? null : id));
  };

  return (
    <section className="dark relative w-full bg-background-950 overflow-hidden">
      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-12 md:py-18">

        {/* Title */}
        <div className="mb-10 md:mb-14">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.1] text-foreground-50 tracking-[-0.02em]">
            Know your enemy.
          </h2>
          <p className="mt-4 text-[15px] text-foreground-400 max-w-[560px] leading-relaxed">
            The same four costs attack every strategy — but each has a completely different weakest link.
          </p>
          <p className="mt-2 text-[12px] text-foreground-400">
            Approximate cost share within each strategy. Based on modeled scenarios — see data below for assumptions.
          </p>
        </div>

        {/* Chart */}
        <div className="relative" onMouseLeave={() => setActiveCost(null)}>

          {/* ── DESKTOP chart (md+) ── */}
          <svg
            viewBox="0 0 800 340"
            className="hidden md:block w-full h-auto"
            style={{ maxHeight: 420 }}
          >
            {/* Y-axis labels and grid */}
            <g className="text-foreground-400" opacity={activeCost ? 0.35 : 0.7}>
              <line x1="96" y1="40" x2="660" y2="40" stroke="currentColor" strokeWidth="0.75" strokeDasharray="2 6" />
              <text x="89" y="44" textAnchor="end" fill="currentColor" fontSize="10">100%</text>
              <line x1="96" y1="103" x2="660" y2="103" stroke="currentColor" strokeWidth="0.75" strokeDasharray="2 6" />
              <text x="89" y="107" textAnchor="end" fill="currentColor" fontSize="10">75%</text>
              <line x1="96" y1="165" x2="660" y2="165" stroke="currentColor" strokeWidth="0.75" strokeDasharray="2 6" />
              <text x="89" y="169" textAnchor="end" fill="currentColor" fontSize="10">50%</text>
              <line x1="96" y1="228" x2="660" y2="228" stroke="currentColor" strokeWidth="0.75" strokeDasharray="2 6" />
              <text x="89" y="232" textAnchor="end" fill="currentColor" fontSize="10">25%</text>
              <text x="89" y="293" textAnchor="end" fill="currentColor" fontSize="10">0%</text>
            </g>
            {/* Y-axis title */}
            <text x="18" y="165" textAnchor="middle" fill="currentColor" className="text-foreground-400" fontSize="10" transform="rotate(-90, 18, 165)" opacity={activeCost ? 0.35 : 0.75}>Cost Share</text>

            {/* Strategy vertical guides */}
            <g className="text-foreground-800" opacity={activeCost ? 0.3 : 1}>
              {xPositions.map((x) => (
                <line key={x} x1={x} y1="40" x2={x} y2="290" stroke="currentColor" strokeWidth="1" strokeDasharray="4 6" />
              ))}
            </g>

            {/* Axis */}
            <line x1="100" y1="290" x2="660" y2="290" stroke="currentColor" className="text-foreground-700" strokeWidth="1" opacity={activeCost ? 0.3 : 1} />

            {/* Strategy labels */}
            <g className="text-foreground-400" opacity={activeCost ? 0.3 : 1}>
              <text x="140" y="318" textAnchor="middle" fill="currentColor" fontSize="11" fontWeight="500">Scalping</text>
              <text x="300" y="318" textAnchor="middle" fill="currentColor" fontSize="11" fontWeight="500">Day Trading</text>
              <text x="460" y="318" textAnchor="middle" fill="currentColor" fontSize="11" fontWeight="500">Swing Trading</text>
              <text x="620" y="318" textAnchor="middle" fill="currentColor" fontSize="11" fontWeight="500">Position Trading</text>
            </g>

            {/* Four cost lines */}
            {lines.map((line) => {
              const isActive = activeCost === line.id;
              const isDimmed = activeCost && !isActive;
              return (
                <g key={line.id} className={line.colorClass} style={{ cursor: 'pointer' }} opacity={isDimmed ? 0.12 : 1}>
                  <path d={line.path} fill="none" stroke="currentColor" strokeWidth={isActive ? line.strokeWidth + 1 : line.strokeWidth} strokeLinecap="round" strokeDasharray={line.strokeDasharray || undefined} pointerEvents="none" />
                  {line.circles.map((c, i) => (
                    <circle key={i} cx={c.cx} cy={c.cy} r={isActive ? c.r + 1 : c.r} fill="currentColor" pointerEvents="none" />
                  ))}
                </g>
              );
            })}

            {/* Invisible hit-area paths */}
            {lines.map((line) => (
              <path key={`hit-${line.id}`} d={line.path} fill="none" stroke="transparent" strokeWidth="30" strokeLinecap="round" style={{ cursor: 'pointer' }} onMouseEnter={() => setActiveCost(line.id)} pointerEvents="all" />
            ))}
          </svg>

          {/* ── MOBILE chart (<md) ── */}
          <div className="block md:hidden">
            <svg
              viewBox="0 0 360 300"
              className="w-full h-auto"
              style={{ minHeight: 200 }}
            >
              {/* Y-axis grid + labels */}
              <g opacity={activeCost ? 0.3 : 0.65}>
                {[
                  { y: 30, label: '100%' },
                  { y: 88, label: '75%' },
                  { y: 146, label: '50%' },
                  { y: 204, label: '25%' },
                  { y: 262, label: '0%' },
                ].map(({ y, label }) => (
                  <g key={y}>
                    <line x1="44" y1={y} x2="345" y2={y} stroke="oklch(var(--foreground-700))" strokeWidth="0.6" strokeDasharray="2 5" />
                    <text x="38" y={y + 4} textAnchor="end" fill="oklch(var(--foreground-500))" fontSize="8.5">{label}</text>
                  </g>
                ))}
              </g>

              {/* Bottom axis line */}
              <line x1="44" y1="262" x2="345" y2="262" stroke="oklch(var(--foreground-700))" strokeWidth="0.8" opacity={activeCost ? 0.3 : 1} />

              {/* Vertical strategy guides */}
              {[86, 172, 258, 344].map((x) => (
                <line key={x} x1={x} y1="30" x2={x} y2="262" stroke="oklch(var(--foreground-800))" strokeWidth="0.8" strokeDasharray="3 5" opacity={activeCost ? 0.2 : 0.9} />
              ))}

              {/* X-axis strategy labels — two-line version */}
              <g opacity={activeCost ? 0.25 : 1}>
                {mobileXLabels.map(([l1, l2], i) => {
                  const x = [86, 172, 258, 344][i];
                  return (
                    <g key={i}>
                      <text x={x} y={l2 ? 279 : 285} textAnchor="middle" fill="oklch(var(--foreground-400))" fontSize="9" fontWeight="500">{l1}</text>
                      {l2 && <text x={x} y={291} textAnchor="middle" fill="oklch(var(--foreground-400))" fontSize="9" fontWeight="500">{l2}</text>}
                    </g>
                  );
                })}
              </g>

              {/* Scaled line paths — original viewBox is ~560 wide (100–660), ~250 tall (40–290).
                  Mobile maps x: [140→344] range [100,660]→[44,344], y: [40,290]→[30,262]
                  Scale x: (x-100)/(560)*300+44   y: (y-40)/(250)*232+30
              */}
              {lines.map((line) => {
                const isActive = activeCost === line.id;
                const isDimmed = activeCost && !isActive;
                const scaleX = (x: number) => ((x - 100) / 560) * 300 + 44;
                const scaleY = (y: number) => ((y - 40) / 250) * 232 + 30;

                // Re-derive path points from circles (simpler than parsing path string)
                const pts = line.circles.map((c) => ({ x: scaleX(c.cx), y: scaleY(c.cy) }));
                // Build smooth cubic bezier
                const scaledPath = pts.length === 4
                  ? `M${pts[0].x},${pts[0].y} C${(pts[0].x + pts[1].x) / 2},${pts[0].y} ${(pts[0].x + pts[1].x) / 2},${pts[1].y} ${pts[1].x},${pts[1].y} C${(pts[1].x + pts[2].x) / 2},${pts[1].y} ${(pts[1].x + pts[2].x) / 2},${pts[2].y} ${pts[2].x},${pts[2].y} C${(pts[2].x + pts[3].x) / 2},${pts[2].y} ${(pts[2].x + pts[3].x) / 2},${pts[3].y} ${pts[3].x},${pts[3].y}`
                  : '';

                return (
                  <g key={line.id} className={line.colorClass} opacity={isDimmed ? 0.1 : 1}>
                    <path
                      d={scaledPath}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={isActive ? line.strokeWidth + 1.5 : line.strokeWidth + 0.5}
                      strokeLinecap="round"
                      strokeDasharray={line.strokeDasharray || undefined}
                      pointerEvents="none"
                    />
                    {pts.map((pt, i) => (
                      <circle key={i} cx={pt.x} cy={pt.y} r={isActive ? line.circles[i].r + 2 : line.circles[i].r + 0.5} fill="currentColor" pointerEvents="none" />
                    ))}
                    {/* Wide tap target */}
                    <path
                      d={scaledPath}
                      fill="none"
                      stroke="transparent"
                      strokeWidth="28"
                      strokeLinecap="round"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleLineTouch(line.id)}
                      pointerEvents="all"
                    />
                  </g>
                );
              })}
            </svg>

            {/* Mobile tap hint */}
            <p className="text-center text-[10px] text-foreground-600 mt-1 mb-3">
              Tap a line to highlight
            </p>
          </div>

          {/* Legend — desktop: single row. Mobile: 2×2 grid */}
          <div className="grid grid-cols-2 md:flex md:flex-wrap md:items-center md:justify-center gap-x-6 gap-y-3 md:gap-x-8 md:gap-y-3 mt-3 md:mt-2">
            {lines.map((line) => {
              const isActive = activeCost === line.id;
              const isDimmed = activeCost && !isActive;

              return (
                <span
                  key={line.id}
                  className="inline-flex items-center gap-2 text-[12px] transition-opacity duration-200"
                  style={{ cursor: 'pointer', opacity: isDimmed ? 0.3 : 1 }}
                  onMouseEnter={() => setActiveCost(line.id)}
                  onMouseLeave={() => setActiveCost(null)}
                  onClick={() => handleLineTouch(line.id)}
                >
                  <svg width="20" height="4" className={line.colorClass} style={{ flexShrink: 0 }}>
                    <line
                      x1="0" y1="2" x2="20" y2="2"
                      stroke="currentColor"
                      strokeWidth={isActive ? 3 : 2}
                      strokeLinecap="butt"
                      strokeDasharray={line.id === 'spread' ? '4 3' : line.id === 'slippage' ? '2 3' : undefined}
                    />
                  </svg>
                  <span className={isActive ? 'text-foreground-200' : 'text-foreground-400'}>
                    {line.label}
                  </span>
                </span>
              );
            })}
          </div>
        </div>

        {/* Insights */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="flex gap-4">
            <div className="w-[3px] flex-shrink-0 bg-primary-500 rounded-full"></div>
            <div>
              <p className="text-[13px] font-[700] text-foreground-200 mb-1.5">Commission collapses</p>
              <p className="text-[13px] text-foreground-400 leading-[1.7]">
                Falls from dominant to negligible across the spectrum. Yet most scalpers comparison-shop by spread alone — ignoring the cost that is 3.5× larger.
              </p>
              <p className="mt-1.5 text-[11px] text-foreground-400 leading-[1.6]">
                Based on EURUSD ECN scalper: 20 RT/day, 0.1 lots, $3.50/side commission, 0.2 pip spread. Commission is $280/mo vs $80/mo spread in this scenario.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-[3px] flex-shrink-0 bg-accent-500 rounded-full"></div>
            <div>
              <p className="text-[13px] font-[700] text-foreground-200 mb-1.5">Swap takes over</p>
              <p className="text-[13px] text-foreground-400 leading-[1.7]">
                Rises from zero to 70% of total costs. The holding cost is the one you never see on the trade ticket until it is too late.
              </p>
              <p className="mt-1.5 text-[11px] text-foreground-400 leading-[1.6]">
                Based on USDJPY position trader: 6 trades/month, 5 lots, avg 30-day hold. Swap is $675/mo — 70% of the total $960/mo cost.
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="w-[3px] flex-shrink-0 bg-secondary-500 rounded-full"></div>
            <div>
              <p className="text-[13px] font-[700] text-foreground-200 mb-1.5">The crossover</p>
              <p className="text-[13px] text-foreground-400 leading-[1.7]">
                Happens between day and swing trading. That is when the dominant cost flips — and most traders never notice the shift.
              </p>
              <p className="mt-1.5 text-[11px] text-foreground-400 leading-[1.6]">
                The tipping point: EURUSD swap costs overtake spread after ~2 days of holding. At 7 days, swap reaches 3.5× the entry spread.
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}